// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * GuidaDichiarazioneRedditi — Deployment 13 spec
 * 6 accordion sections + fiscal disclaimer + link to detrazioni-730.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, FileText, Calculator, AlertTriangle } from 'lucide-react';

const SECTIONS = [
  {
    id: 'chi',
    title: 'Chi deve presentarla',
    icon: '👥',
    body: 'chi',
  },
  {
    id: 'scadenze',
    title: 'Scadenze 2026',
    icon: '📅',
    body: 'scadenze',
  },
  {
    id: 'documenti',
    title: 'Documenti necessari',
    icon: '📑',
    body: 'documenti',
  },
  {
    id: 'detrazioni',
    title: 'Le detrazioni principali',
    icon: '💸',
    body: 'detrazioni',
  },
  {
    id: 'compilare',
    title: 'Come compilare il 730',
    icon: '🖥️',
    body: 'compilare',
  },
  {
    id: 'errori',
    title: 'Errori comuni da evitare',
    icon: '⚠️',
    body: 'errori',
  },
];

// ─── Section 1: Chi deve presentarla ───────────────────────────────────
const SectionChi = () => (
  <div className="space-y-3 text-sm leading-relaxed">
    <p className="text-[#E8E8E4] font-medium">Chi deve presentare la dichiarazione:</p>
    <ul className="list-disc list-inside text-[#888780] space-y-1.5">
      <li>Lavoratori dipendenti con più fonti di reddito</li>
      <li>Autonomi e P.IVA in regime ordinario</li>
      <li>Chi ha redditi da affitto o investimenti</li>
      <li>Chi vuole recuperare detrazioni</li>
    </ul>
    <p className="text-[#E8E8E4] font-medium pt-2">Chi NON deve presentarla:</p>
    <p className="text-[#888780]">
      Lavoratori dipendenti con un solo datore di lavoro e nessuna detrazione aggiuntiva.
    </p>
  </div>
);

// ─── Section 2: Scadenze 2026 ───────────────────────────────────────────
const SectionScadenze = () => (
  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
    <table className="w-full text-sm" data-testid="scadenze-table">
      <thead>
        <tr style={{ background: '#252523' }}>
          <th className="text-left p-3 text-[#E8E8E4] font-semibold">Scadenza</th>
          <th className="text-left p-3 text-[#E8E8E4] font-semibold">Data</th>
        </tr>
      </thead>
      <tbody className="text-[#888780]">
        <tr className="border-t border-white/[0.06]"><td className="p-3">730 precompilato disponibile</td><td className="p-3">30 aprile 2026</td></tr>
        <tr className="border-t border-white/[0.06]"><td className="p-3">Invio 730 (CAF/sostituto)</td><td className="p-3">30 settembre 2026</td></tr>
        <tr className="border-t border-white/[0.06]"><td className="p-3">Modello Redditi PF telematico</td><td className="p-3">30 novembre 2026</td></tr>
        <tr className="border-t border-white/[0.06]"><td className="p-3">Modello Redditi PF cartaceo</td><td className="p-3">31 ottobre 2026 <span className="italic">(salvo proroghe ministeriali)</span></td></tr>
        <tr className="border-t border-white/[0.06]"><td className="p-3">Versamento saldo IRPEF</td><td className="p-3">30 giugno 2026</td></tr>
        <tr className="border-t border-white/[0.06]"><td className="p-3">Versamento con maggiorazione 0,4%</td><td className="p-3">30 luglio 2026 <span className="italic">(solo per chi paga dopo il 30 giugno)</span></td></tr>
      </tbody>
    </table>
  </div>
);

// ─── Section 3: Documenti necessari ─────────────────────────────────────
const SectionDocumenti = () => (
  <ul className="list-disc list-inside text-[#888780] space-y-1.5 text-sm">
    <li>CU dal datore di lavoro</li>
    <li>Scontrini/fatture spese mediche</li>
    <li>Interessi passivi mutuo (certificato banca)</li>
    <li>Quietanze assicurazioni vita/infortuni</li>
    <li>Ricevute contributi previdenza complementare</li>
    <li>Documentazione immobili</li>
    <li>ISEE se richiesto per bonus</li>
  </ul>
);

// ─── Section 4: Le detrazioni principali ────────────────────────────────
const SectionDetrazioni = () => (
  <div className="space-y-4">
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
      <table className="w-full text-sm" data-testid="detrazioni-table">
        <thead>
          <tr style={{ background: '#252523' }}>
            <th className="text-left p-3 text-[#E8E8E4] font-semibold">Detrazione</th>
            <th className="text-left p-3 text-[#E8E8E4] font-semibold">%</th>
            <th className="text-left p-3 text-[#E8E8E4] font-semibold">Limite/Note</th>
          </tr>
        </thead>
        <tbody className="text-[#888780]">
          <tr className="border-t border-white/[0.06]"><td className="p-3">Spese mediche</td><td className="p-3">19%</td><td className="p-3">Eccedente €129,11</td></tr>
          <tr className="border-t border-white/[0.06]"><td className="p-3">Interessi mutuo</td><td className="p-3">19%</td><td className="p-3">Max €4.000/anno (solo mutui acquisto abitazione principale)</td></tr>
          <tr className="border-t border-white/[0.06]"><td className="p-3">Pensione complementare</td><td className="p-3">deducibile</td><td className="p-3">Max €5.164,57/anno</td></tr>
          <tr className="border-t border-white/[0.06]"><td className="p-3">Ristrutturazione edilizia</td><td className="p-3">50%/36%</td><td className="p-3">Max €96.000 (50% prima casa, 36% seconda casa)</td></tr>
          <tr className="border-t border-white/[0.06]"><td className="p-3">Bonus mobili</td><td className="p-3">50%</td><td className="p-3">Max €5.000 (solo se collegato a intervento di ristrutturazione)</td></tr>
          <tr className="border-t border-white/[0.06]"><td className="p-3">Assicurazioni vita/infortuni</td><td className="p-3">19%</td><td className="p-3">Max €530 (solo polizze rischio morte o invalidità permanente ≥5%)</td></tr>
        </tbody>
      </table>
    </div>
    <p className="text-xs text-[#888780] italic" data-testid="detrazioni-uni-note">
      ℹ️ Per le spese universitarie private la detrazione è del 19% con limiti variabili per ateneo e corso definiti dal MIUR. Consulta il tuo CAF per l'importo esatto.
    </p>
    <Link to="/calcolatori/detrazioni-730"
      data-testid="link-calcolatore-detrazioni"
      className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C] hover:text-[#E8E8E4] transition-colors">
      <Calculator className="w-4 h-4" /> Calcola le tue detrazioni →
    </Link>
  </div>
);

// ─── Section 5: Come compilare il 730 ───────────────────────────────────
const SectionCompilare = () => (
  <div className="text-sm text-[#888780] space-y-2.5">
    <ol className="list-decimal list-inside space-y-2">
      <li>Accedi a <a href="https://www.agenziaentrate.gov.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline" data-testid="link-agenzia-entrate">https://www.agenziaentrate.gov.it</a> con SPID o CIE</li>
      <li>Verifica i dati precompilati</li>
      <li>Aggiungi le spese detraibili con relativi documenti</li>
      <li>Controlla il risultato (rimborso o importo dovuto)</li>
      <li>Invia o porta al CAF per assistenza</li>
    </ol>
    <p className="italic text-xs pt-2">Nota: il 730 precompilato è gratuito e disponibile direttamente sul sito dell'Agenzia delle Entrate.</p>
  </div>
);

// ─── Section 6: Errori comuni ───────────────────────────────────────────
const SectionErrori = () => (
  <ul className="list-disc list-inside text-[#888780] space-y-1.5 text-sm">
    <li>Non inserire spese senza documentazione</li>
    <li>Dimenticare le spese mediche dei familiari a carico</li>
    <li>Non indicare redditi da affitto</li>
    <li>Perdere la scadenza (sanzione minima €250)</li>
    <li>Non verificare i dati precompilati (possono contenere errori)</li>
  </ul>
);

const renderBody = (id) => {
  switch (id) {
    case 'chi':        return <SectionChi />;
    case 'scadenze':   return <SectionScadenze />;
    case 'documenti':  return <SectionDocumenti />;
    case 'detrazioni': return <SectionDetrazioni />;
    case 'compilare':  return <SectionCompilare />;
    case 'errori':     return <SectionErrori />;
    default:           return null;
  }
};

export default function GuidaDichiarazioneRedditi() {
  const [open, setOpen] = useState(null);
  return (
    <div className="min-h-screen bg-[#1C1C1A] py-12 px-4" data-testid="guida-dichiarazione-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
        <div className="mb-8">
          <p className="text-xs text-[#C9A84C] font-semibold tracking-widest uppercase mb-2">Guida</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#E8E8E4] mb-3 flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#C9A84C]" /> Guida alla Dichiarazione dei Redditi
          </h1>
          <p className="text-base text-[#888780]">Tutto quello che devi sapere per il 730 e il Modello Redditi 2026</p>
        </div>

        <div className="space-y-3" data-testid="guida-dichiarazione-accordions">
          {SECTIONS.map((s, i) => (
            <div key={s.id} className="rounded-xl overflow-hidden" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`section-${s.id}`}>
              <button
                onClick={() => setOpen(open === s.id ? null : s.id)}
                data-testid={`section-toggle-${s.id}`}
                className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className="text-2xl">{s.icon}</span>
                <span className="flex-1 font-semibold text-[#E8E8E4]">{i + 1}. {s.title}</span>
                <ChevronDown className={`w-5 h-5 text-[#C9A84C] transition-transform ${open === s.id ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {open === s.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}>
                    <div className="px-4 pb-5 pt-1">
                      {renderBody(s.body)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Fiscal disclaimer at bottom */}
        <div className="mt-8 p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }} data-testid="fiscal-disclaimer">
          <AlertTriangle className="w-5 h-5 text-[#C9A84C] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#888780] leading-relaxed">
            ⚠️ Le informazioni presenti in questa guida hanno scopo esclusivamente educativo e non costituiscono consulenza fiscale. Prima di prendere decisioni fiscali, consulta un commercialista o un professionista fiscale abilitato.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
